import {
  CheckCircleIcon,
  ProhibitIcon,
  WarningCircleIcon,
  XCircleIcon,
} from '@phosphor-icons/react';
import { T, Var, useGT } from 'gt-react';
import { cn } from '@/lib/utils';
import type { BulkSendItem, BulkSendResult } from './types';

interface BulkSendSummaryListProps<TItem extends BulkSendItem> {
  items: TItem[];
  results: Map<string, BulkSendResult>;
  onRevisit: (index: number) => void;
}

export function BulkSendSummaryList<TItem extends BulkSendItem>({
  items,
  results,
  onRevisit,
}: BulkSendSummaryListProps<TItem>) {
  const gt = useGT();
  const resultEntries = Array.from(results.entries());
  const sentCount = resultEntries.filter(([, r]) => r.status === 'sent').length;
  const skippedCount = resultEntries.filter(
    ([, r]) => r.status === 'skipped',
  ).length;
  const canceledCount = resultEntries.filter(
    ([, r]) => r.status === 'canceled',
  ).length;
  const failedCount = resultEntries.filter(
    ([, r]) => r.status === 'failed',
  ).length;

  return (
    <div className="min-h-0 flex-1 flex flex-col gap-4 overflow-auto py-2">
      <div className="flex items-center gap-4 text-sm">
        {sentCount > 0 && (
          <span className="flex items-center gap-1 text-green-700">
            <CheckCircleIcon className="h-4 w-4" weight="fill" />
            <T context="bulk send summary count">
              <Var>{sentCount}</Var> sent
            </T>
          </span>
        )}
        {skippedCount > 0 && (
          <span className="flex items-center gap-1 text-muted-foreground">
            <WarningCircleIcon className="h-4 w-4" />
            <T context="bulk send summary count">
              <Var>{skippedCount}</Var> skipped
            </T>
          </span>
        )}
        {canceledCount > 0 && (
          <span className="flex items-center gap-1 text-muted-foreground">
            <ProhibitIcon className="h-4 w-4" />
            <T context="bulk send summary count">
              <Var>{canceledCount}</Var> canceled
            </T>
          </span>
        )}
        {failedCount > 0 && (
          <span className="flex items-center gap-1 text-red-700">
            <XCircleIcon className="h-4 w-4" weight="fill" />
            <T context="bulk send summary count">
              <Var>{failedCount}</Var> failed
            </T>
          </span>
        )}
      </div>

      <div className="space-y-1">
        {items.map((item, index) => {
          const result = results.get(item.id);
          if (!result) return null;
          const canRevisit =
            result.status === 'skipped' ||
            result.status === 'failed' ||
            result.status === 'canceled';
          const rowClassName = cn(
            'flex w-full items-center justify-between gap-3 rounded-md border px-3 py-2 text-left text-sm',
            canRevisit && 'hover:bg-muted cursor-pointer',
          );
          const rowBody = (
            <>
              <span className="font-medium truncate">
                {result.itemName ??
                  gt('(Unknown)', { context: 'unknown item name placeholder' })}
              </span>
              <div className="flex items-center gap-3 shrink-0">
                <span
                  className={
                    result.status === 'sent'
                      ? 'text-green-700'
                      : result.status === 'failed'
                        ? 'text-red-700'
                        : 'text-muted-foreground'
                  }
                >
                  {result.status === 'sent' && (
                    <T context="bulk send result status">Sent</T>
                  )}
                  {result.status === 'skipped' && (
                    <T context="bulk send result status">Skipped</T>
                  )}
                  {result.status === 'canceled' && (
                    <T context="bulk send result status">Canceled</T>
                  )}
                  {result.status === 'failed' && (
                    <T context="bulk send result status, with error detail">
                      Failed:{' '}
                      <Var>
                        {result.error ??
                          gt('Unknown error', {
                            context: 'fallback error message',
                          })}
                      </Var>
                    </T>
                  )}
                </span>
                {canRevisit && (
                  <span className="text-xs text-muted-foreground">
                    <T context="hint shown next to revisitable summary rows">
                      Click to revisit
                    </T>
                  </span>
                )}
              </div>
            </>
          );
          return canRevisit ? (
            <button
              key={item.id}
              type="button"
              onClick={() => onRevisit(index)}
              className={rowClassName}
            >
              {rowBody}
            </button>
          ) : (
            <div key={item.id} className={rowClassName}>
              {rowBody}
            </div>
          );
        })}
      </div>
    </div>
  );
}
